import type { CSSObject, Palette } from '@mui/material/styles';
import { alpha, decomposeColor } from '@mui/material/styles';

// The same guard the theme puts on monochrome hover fills: touch devices hold
// `:hover` after a tap until the next tap elsewhere, which would leave the
// fill stuck on.
const hoverCapable = '@media (hover: hover) and (pointer: fine)';
function parsesAsColor(value: string): boolean {
  try {
    decomposeColor(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Styles for the top bar, from the theme's optional `background.header` and
 * `text.header`. Both are unset by default, and the bar then keeps the surface
 * and text colour it would have anyway — so this returns nothing.
 *
 * Several controls on the bar carry the page's text colour in the theme or in
 * their own styles rather than inheriting it, so they are pointed at the bar's
 * text colour here. Their own styles are inserted after the bar's, so the
 * selectors repeat a class where they need to outrank them.
 */
export function headerBarStyles(palette: Palette): CSSObject {
  const background = palette.background.header;
  const text = palette.text.header;

  // The theme comes from unvalidated tenant config, and `alpha` throws on a
  // value it cannot read. Such a value is still applied as the text colour,
  // where the browser decides what it means; only the tints drawn from it
  // are left out.
  const tint = (opacity: number) => text && parsesAsColor(text) ? alpha(text, opacity) : undefined;
  return {
    ...(background && {
      backgroundColor: background,
      // Paper's dark-mode elevation overlay would tint the chosen colour.
      backgroundImage: 'none'
    }),
    ...(text && {
      color: text,
      // Text set to the page's secondary colour, such as the event date on
      // mobile, follows the bar instead.
      '& .MuiTypography-root': {
        color: 'inherit'
      },
      // The account pages' tabs.
      '& .MuiTab-root.MuiTab-root': {
        color: tint(0.7) ?? text,
        '&.Mui-selected': {
          color: text
        },
        '@media (hover: hover)': {
          '&:hover': {
            color: text
          }
        }
      },
      // Monochrome buttons, icon-only ones included, plus the language
      // selector's icon button on mobile.
      '& .MuiButton-colorMonochrome, & .MuiIconButton-colorDefault': {
        color: text,
        [hoverCapable]: {
          '&:not(.MuiButton-contained):hover': {
            backgroundColor: tint(0.08)
          }
        }
      },
      '& .MuiButton-colorMonochrome.MuiButton-outlined': {
        borderColor: tint(0.24)
      },
      // Contained ones, like the order timer, sit on a faint fill of the page's
      // text colour and switch back to that colour on hover — on any device,
      // as the theme does for them.
      '& .MuiButton-colorMonochrome.MuiButton-contained': {
        backgroundColor: tint(0.08),
        '&:hover': {
          color: text,
          backgroundColor: tint(0.14)
        }
      }
    })
  };
}